import {
  MeshPhysicalMaterial,
  MeshPhongMaterial,
  Color,
  TextureLoader,
  RepeatWrapping,
} from 'three';

const NOISE_TEXTURE_URL = '/img/random_noise_tileable_256x256.jpg';

const createGlassGradientMaterial = ({
  gradientColors,
  envMap,
  pointerLightIntensity = 0.2,
  normedGradientMiddle = 0.5,
  noiseAmount = 0.1,
}) => {
  const { gradientColorBottom, gradientColorTop, gradientColorMiddle } =
    gradientColors;

  const glassMaterial = new MeshPhysicalMaterial({
    roughness: 0.1,
    transmission: 0,
    thickness: 0,
    ior: 1.7,
    clearcoat: 0.25,
    clearcoatRoughness: 0.45,
    color: new Color(0x000000),
    envMap: envMap,
  });

  let customGlassUniforms = {
    uTime: { value: 0 },
    uGradientColors: {
      value: [gradientColorBottom, gradientColorTop, gradientColorMiddle],
    },
    uResolution: {
      value: [0, 0],
    },
    uScissorOffsets: {
      value: [0, 0],
    },
    uPixelRatio: {
      value: 0,
    },
    uMousePointer: {
      value: [0, 0],
    },
    uPointerLightIntensity: {
      value: pointerLightIntensity,
    },
    uNormedGradientMiddle: {
      value: normedGradientMiddle,
    },
    uNoiseAmount: {
      value: noiseAmount,
    },
  };

  glassMaterial.onBeforeCompile = (shader) => {
    shader.uniforms.uMousePointer = customGlassUniforms.uMousePointer;
    shader.uniforms.uResolution = customGlassUniforms.uResolution;
    shader.uniforms.uScissorOffsets = customGlassUniforms.uScissorOffsets;
    shader.uniforms.uPointerLightIntensity =
      customGlassUniforms.uPointerLightIntensity;
    shader.uniforms.uTime = customGlassUniforms.uTime;
    shader.uniforms.uGradientColors = customGlassUniforms.uGradientColors;
    shader.uniforms.uPixelRatio = customGlassUniforms.uPixelRatio;
    shader.uniforms.uNormedGradientMiddle =
      customGlassUniforms.uNormedGradientMiddle;
    shader.uniforms.uNoiseAmount = customGlassUniforms.uNoiseAmount;

    shader.vertexShader = shader.vertexShader
      .replace(
        '#define STANDARD',
        /* glsl */ `
        #define STANDARD
        varying vec2 vcUv;
        varying vec3 vcN;
      `
      )
      .replace(
        '#include <uv_vertex>',
        /* glsl */ `
        #include <uv_vertex>

        vcUv = uv;
        vcN = normal;
      `
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        'uniform float opacity;',
        /* glsl */ `
        uniform float opacity;
        uniform float uTime;
        uniform float uPointerLightIntensity;
        uniform float uPixelRatio;
        uniform float uNormedGradientMiddle;
        uniform float uNoiseAmount;

        uniform vec2 uMousePointer;
        uniform vec2 uResolution;
        uniform vec2 uScissorOffsets;

        uniform vec3 uGradientColors[3];

        varying vec2 vcUv;
        varying vec3 vcN;

        float noise(vec2 co) {
            return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
        }
      `
      )
      .replace(
        '#include <dithering_fragment>',
        /* glsl */ `
        #include <dithering_fragment>

        vec2 xy = vcUv.yx;

        vec3 firstColor = uGradientColors[0];
        vec3 middleColor = uGradientColors[2];
        vec3 secondColor = uGradientColors[1];

        vec3 col = vec3(1., 1., 1.);
        float h = uNormedGradientMiddle;

        if(vcN.g > 0.5) { // top of bar
          col = secondColor;
        } else if(vcN.g < -0.5) {  // bottom of bar
          col = firstColor;
        } else {
          col = mix(
              mix(firstColor, middleColor, xy.x/h),
              mix(middleColor, secondColor, (xy.x - h)/(1.0 - h)),
              step(h, xy.x)
          );
        }

        gl_FragColor.rgb = mix(gl_FragColor.rgb, col, 0.5);

        // vec2 st = (gl_FragCoord.xy/uResolution.xy)/uPixelRatio;
        vec2 fragCoord = vec2(
          gl_FragCoord.x - (uScissorOffsets.x * uPixelRatio),
          gl_FragCoord.y - (uScissorOffsets.y * uPixelRatio)
        );
        vec2 st = (fragCoord.xy/uResolution.xy)/uPixelRatio;

        float aspect = uResolution.y/uResolution.x;
        st.y *= aspect;

        vec2 mp = uMousePointer;
        mp.y *= aspect;

        float bv = clamp(
          1. - (distance(mp, st)*7.),
          0.,
          1.
        );

        vec3 lightColor = vec3(bv*0.2) * uPointerLightIntensity;
        gl_FragColor.rgb += (gl_FragColor.rgb * lightColor * 3.);

        vec3 n = vec3(noise(st*vcN.x + st*vcN.z + st*vcN.y));
        gl_FragColor.rgb = mix(gl_FragColor.rgb, n, uNoiseAmount);
      `
      );
  };

  return {
    glassMaterial,
    customGlassUniforms,
  };
};

const createPhongGradientMaterial = ({
  gradientColors,
  envMap,
  pointerLightIntensity = 0.2,
  normedGradientMiddle = 0.5,
  noiseAmount = 0.1,
}) => {
  const noiseTexture = new TextureLoader().load(NOISE_TEXTURE_URL);
  noiseTexture.wrapS = RepeatWrapping;
  noiseTexture.wrapT = RepeatWrapping;

  const { gradientColorBottom, gradientColorTop, gradientColorMiddle } =
    gradientColors;

  const glassMaterial = new MeshPhongMaterial({
    color: new Color(0x000000),
  });

  let customGlassUniforms = {
    uTime: { value: 0 },
    uGradientColors: {
      value: [gradientColorBottom, gradientColorTop, gradientColorMiddle],
    },
    uResolution: {
      value: [0, 0],
    },
    uScissorOffsets: {
      value: [0, 0],
    },
    uPixelRatio: {
      value: 0,
    },
    uMousePointer: {
      value: [0, 0],
    },
    uPointerLightIntensity: {
      value: pointerLightIntensity,
    },
    uNormedGradientMiddle: {
      value: normedGradientMiddle,
    },
    uNoiseAmount: {
      value: noiseAmount,
    },
    uNoiseTexture: {
      value: noiseTexture,
    },
  };

  glassMaterial.onBeforeCompile = (shader) => {
    shader.uniforms.uMousePointer = customGlassUniforms.uMousePointer;
    shader.uniforms.uResolution = customGlassUniforms.uResolution;
    shader.uniforms.uScissorOffsets = customGlassUniforms.uScissorOffsets;
    shader.uniforms.uPointerLightIntensity =
      customGlassUniforms.uPointerLightIntensity;
    shader.uniforms.uTime = customGlassUniforms.uTime;
    shader.uniforms.uGradientColors = customGlassUniforms.uGradientColors;
    shader.uniforms.uPixelRatio = customGlassUniforms.uPixelRatio;
    shader.uniforms.uNormedGradientMiddle =
      customGlassUniforms.uNormedGradientMiddle;
    shader.uniforms.uNoiseAmount = customGlassUniforms.uNoiseAmount;
    shader.uniforms.uNoiseTexture = customGlassUniforms.uNoiseTexture;

    shader.vertexShader = shader.vertexShader
      .replace(
        '#define PHONG',
        /* glsl */ `
        #define PHONG
        varying vec2 vcUv;
        varying vec3 vcN;
      `
      )
      .replace(
        '#include <uv_vertex>',
        /* glsl */ `
        #include <uv_vertex>

        vcUv = uv;
        vcN = normal;
      `
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        'uniform float opacity;',
        /* glsl */ `
        uniform float opacity;
        uniform float uTime;
        uniform float uPointerLightIntensity;
        uniform float uPixelRatio;
        uniform float uNormedGradientMiddle;
        uniform float uNoiseAmount;

        uniform vec2 uMousePointer;
        uniform vec2 uResolution;
        uniform vec2 uScissorOffsets;

        uniform vec3 uGradientColors[3];

        uniform sampler2D uNoiseTexture;

        varying vec2 vcUv;
        varying vec3 vcN;

        // float noise(vec2 co) {
        //     return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
        // }

        float noise(vec2 uv) {
          return texture2D(uNoiseTexture, uv * 3.5).r;
        }
      `
      )
      .replace(
        '#include <dithering_fragment>',
        /* glsl */ `
        #include <dithering_fragment>

        vec2 xy = vcUv.yx;

        vec3 firstColor = uGradientColors[0];
        vec3 middleColor = uGradientColors[2];
        vec3 secondColor = uGradientColors[1];

        vec3 col = vec3(1., 1., 1.);
        float h = uNormedGradientMiddle;

        if(vcN.g > 0.5) { // top of bar
          col = secondColor;
        } else if(vcN.g < -0.5) {  // bottom of bar
          col = firstColor;
        } else {
          col = mix(
              mix(firstColor, middleColor, xy.x/h),
              mix(middleColor, secondColor, (xy.x - h)/(1.0 - h)),
              step(h, xy.x)
          );
        }

        gl_FragColor.rgb = mix(gl_FragColor.rgb, col, 0.5);

        vec2 fragCoord = vec2(
          gl_FragCoord.x - (uScissorOffsets.x * uPixelRatio),
          gl_FragCoord.y - (uScissorOffsets.y * uPixelRatio)
        );

        vec2 st = (fragCoord.xy/uResolution.xy)/uPixelRatio;
        float aspect = uResolution.y/uResolution.x;
        st.y *= aspect;

        vec2 mp = uMousePointer;
        mp.y *= aspect;

        float bv = clamp(
          1. - (distance(mp, st)*7.),
          0.,
          1.
        );

        vec3 lightColor = vec3(bv*0.2) * uPointerLightIntensity;
        gl_FragColor.rgb += (gl_FragColor.rgb * lightColor * 3.);

        vec3 n = vec3(noise(st*vcN.x + st*vcN.z + st*vcN.y));
        gl_FragColor.rgb = mix(gl_FragColor.rgb, n, uNoiseAmount);
      `
      );
  };

  return {
    glassMaterial,
    customGlassUniforms,
  };
};

const createHorizontalGlassGradientMaterial = ({
  gradientColors,
  envMap,
  pointerLightIntensity = 0.2,
  normedGradientMiddle = 0.5,
  noiseAmount = 0.1,
}) => {
  const { gradientColorBottom, gradientColorTop, gradientColorMiddle } =
    gradientColors;

  const glassMaterial = new MeshPhysicalMaterial({
    roughness: 0.1,
    transmission: 0,
    thickness: 0,
    ior: 1.7,
    clearcoat: 0.25,
    clearcoatRoughness: 0.45,
    color: new Color(0x000000),
    envMap: envMap,
  });

  let customGlassUniforms = {
    uTime: { value: 0 },
    uGradientColors: {
      value: [gradientColorBottom, gradientColorTop, gradientColorMiddle],
    },
    uResolution: {
      value: [0, 0],
    },
    uScissorOffsets: {
      value: [0, 0],
    },
    uPixelRatio: {
      value: 0,
    },
    uMousePointer: {
      value: [0, 0],
    },
    uPointerLightIntensity: {
      value: pointerLightIntensity,
    },
    uNormedGradientMiddle: {
      value: normedGradientMiddle,
    },
    uNoiseAmount: {
      value: noiseAmount,
    },
  };

  glassMaterial.onBeforeCompile = (shader) => {
    shader.uniforms.uMousePointer = customGlassUniforms.uMousePointer;
    shader.uniforms.uResolution = customGlassUniforms.uResolution;
    shader.uniforms.uScissorOffsets = customGlassUniforms.uScissorOffsets;
    shader.uniforms.uPointerLightIntensity =
      customGlassUniforms.uPointerLightIntensity;
    shader.uniforms.uTime = customGlassUniforms.uTime;
    shader.uniforms.uGradientColors = customGlassUniforms.uGradientColors;
    shader.uniforms.uPixelRatio = customGlassUniforms.uPixelRatio;
    shader.uniforms.uNormedGradientMiddle =
      customGlassUniforms.uNormedGradientMiddle;
    shader.uniforms.uNoiseAmount = customGlassUniforms.uNoiseAmount;

    shader.vertexShader = shader.vertexShader
      .replace(
        '#define STANDARD',
        /* glsl */ `
        #define STANDARD
        varying vec2 vcUv;
        varying vec3 vcN;
      `
      )
      .replace(
        '#include <uv_vertex>',
        /* glsl */ `
        #include <uv_vertex>

        vcUv = uv;
        vcN = normal;
      `
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        'uniform float opacity;',
        /* glsl */ `
        uniform float opacity;
        uniform float uTime;
        uniform float uPointerLightIntensity;
        uniform float uPixelRatio;
        uniform float uNormedGradientMiddle;
        uniform float uNoiseAmount;

        uniform vec2 uMousePointer;
        uniform vec2 uResolution;
        uniform vec2 uScissorOffsets;

        uniform vec3 uGradientColors[3];

        varying vec2 vcUv;
        varying vec3 vcN;

        float noise(vec2 co) {
            return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
        }
      `
      )
      .replace(
        '#include <dithering_fragment>',
        /* glsl */ `
        #include <dithering_fragment>

        vec2 xy = vcUv.yx;

        vec3 firstColor = uGradientColors[0];
        vec3 middleColor = uGradientColors[2];
        vec3 secondColor = uGradientColors[1];

        vec3 col = vec3(1., 1., 1.);
        float h = uNormedGradientMiddle;

        if(vcN.r > 0.5) { // right side of bar
          col = firstColor;
        } else if(vcN.r <= -0.5) {  // left side of bar
          col = secondColor;
        } else {
          if(vcN.b > -0.5) {
            col = mix(
              mix(secondColor, middleColor, xy.y/h),
              mix(middleColor, firstColor, (xy.y - h)/(1.0 - h)),
              step(h, xy.y)
            );
          } else {
            vec2 flippedXy = vec2(xy.x, 1. - xy.y);
            col = mix(
              mix(secondColor, middleColor, flippedXy.y/h),
              mix(middleColor, firstColor, (flippedXy.y - h)/(1.0 - h)),
              step(h, flippedXy.y)
            );
          }
        }

        gl_FragColor.rgb = mix(gl_FragColor.rgb, col, 0.5);

        vec2 fragCoord = vec2(
          gl_FragCoord.x - (uScissorOffsets.x * uPixelRatio),
          gl_FragCoord.y - (uScissorOffsets.y * uPixelRatio)
        );

        // vec2 st = (gl_FragCoord.xy/uResolution.xy)/uPixelRatio;
        vec2 st = (fragCoord.xy/uResolution.xy)/uPixelRatio;
        float aspect = uResolution.y/uResolution.x;
        st.y *= aspect;

        vec2 mp = uMousePointer;
        mp.y *= aspect;

        float bv = clamp(
          1. - (distance(mp, st)*7.),
          0.,
          1.
        );

        vec3 lightColor = vec3(bv*0.2) * uPointerLightIntensity;
        gl_FragColor.rgb += (gl_FragColor.rgb * lightColor * 3.);

        vec3 n = vec3(noise(st*vcN.x + st*vcN.z + st*vcN.y));
        gl_FragColor.rgb = mix(gl_FragColor.rgb, n, uNoiseAmount);
      `
      );
  };

  return {
    glassMaterial,
    customGlassUniforms,
  };
};

const createHorizontalPhongGradientMaterial = ({
  gradientColors,
  envMap,
  pointerLightIntensity = 0.2,
  normedGradientMiddle = 0.5,
  noiseAmount = 0.1,
}) => {
  const noiseTexture = new TextureLoader().load(NOISE_TEXTURE_URL);
  noiseTexture.wrapS = RepeatWrapping;
  noiseTexture.wrapT = RepeatWrapping;

  const { gradientColorBottom, gradientColorTop, gradientColorMiddle } =
    gradientColors;

  const glassMaterial = new MeshPhongMaterial({
    color: new Color(0x000000),
  });

  let customGlassUniforms = {
    uTime: { value: 0 },
    uGradientColors: {
      value: [gradientColorBottom, gradientColorTop, gradientColorMiddle],
    },
    uResolution: {
      value: [0, 0],
    },
    uScissorOffsets: {
      value: [0, 0],
    },
    uPixelRatio: {
      value: 0,
    },
    uMousePointer: {
      value: [0, 0],
    },
    uPointerLightIntensity: {
      value: pointerLightIntensity,
    },
    uNormedGradientMiddle: {
      value: normedGradientMiddle,
    },
    uNoiseAmount: {
      value: noiseAmount,
    },
    uNoiseTexture: {
      value: noiseTexture,
    },
  };

  glassMaterial.onBeforeCompile = (shader) => {
    shader.uniforms.uMousePointer = customGlassUniforms.uMousePointer;
    shader.uniforms.uResolution = customGlassUniforms.uResolution;
    shader.uniforms.uScissorOffsets = customGlassUniforms.uScissorOffsets;
    shader.uniforms.uPointerLightIntensity =
      customGlassUniforms.uPointerLightIntensity;
    shader.uniforms.uTime = customGlassUniforms.uTime;
    shader.uniforms.uGradientColors = customGlassUniforms.uGradientColors;
    shader.uniforms.uPixelRatio = customGlassUniforms.uPixelRatio;
    shader.uniforms.uNormedGradientMiddle =
      customGlassUniforms.uNormedGradientMiddle;
    shader.uniforms.uNoiseAmount = customGlassUniforms.uNoiseAmount;
    shader.uniforms.uNoiseTexture = customGlassUniforms.uNoiseTexture;

    shader.vertexShader = shader.vertexShader
      .replace(
        '#define PHONG',
        /* glsl */ `
        #define PHONG
        varying vec2 vcUv;
        varying vec3 vcN;
      `
      )
      .replace(
        '#include <uv_vertex>',
        /* glsl */ `
        #include <uv_vertex>

        vcUv = uv;
        vcN = normal;
      `
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        'uniform float opacity;',
        /* glsl */ `
        uniform float opacity;
        uniform float uTime;
        uniform float uPointerLightIntensity;
        uniform float uPixelRatio;
        uniform float uNormedGradientMiddle;
        uniform float uNoiseAmount;

        uniform vec2 uMousePointer;
        uniform vec2 uResolution;
        uniform vec2 uScissorOffsets;

        uniform vec3 uGradientColors[3];

        uniform sampler2D uNoiseTexture;

        varying vec2 vcUv;
        varying vec3 vcN;

        // float noise(vec2 co) {
        //     return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
        // }

        float noise(vec2 uv) {
          return texture2D(uNoiseTexture, uv * 3.5).r;
        }
      `
      )
      .replace(
        '#include <dithering_fragment>',
        /* glsl */ `
        #include <dithering_fragment>

        vec2 xy = vcUv.yx;

        vec3 firstColor = uGradientColors[0];
        vec3 middleColor = uGradientColors[2];
        vec3 secondColor = uGradientColors[1];

        vec3 col = vec3(1., 1., 1.);
        float h = uNormedGradientMiddle;

        if(vcN.r > 0.5) { // right side of bar
          col = firstColor;
        } else if(vcN.r <= -0.5) {  // left side of bar
          col = secondColor;
        } else {
          if(vcN.b > -0.5) {
            col = mix(
              mix(secondColor, middleColor, xy.y/h),
              mix(middleColor, firstColor, (xy.y - h)/(1.0 - h)),
              step(h, xy.y)
            );
          } else {
            vec2 flippedXy = vec2(xy.x, 1. - xy.y);
            col = mix(
              mix(secondColor, middleColor, flippedXy.y/h),
              mix(middleColor, firstColor, (flippedXy.y - h)/(1.0 - h)),
              step(h, flippedXy.y)
            );
          }
        }

        gl_FragColor.rgb = mix(gl_FragColor.rgb, col, 0.5);

        vec2 fragCoord = vec2(
          gl_FragCoord.x - (uScissorOffsets.x * uPixelRatio),
          gl_FragCoord.y - (uScissorOffsets.y * uPixelRatio)
        );

        vec2 st = (fragCoord.xy/uResolution.xy)/uPixelRatio;
        float aspect = uResolution.y/uResolution.x;
        st.y *= aspect;

        vec2 mp = uMousePointer;
        mp.y *= aspect;

        float bv = clamp(
          1. - (distance(mp, st)*7.),
          0.,
          1.
        );

        vec3 lightColor = vec3(bv*0.2) * uPointerLightIntensity;
        gl_FragColor.rgb += (gl_FragColor.rgb * lightColor * 3.);

        vec3 n = vec3(noise(st*vcN.x + st*vcN.z + st*vcN.y));
        gl_FragColor.rgb = mix(gl_FragColor.rgb, n, uNoiseAmount);
      `
      );
  };

  return {
    glassMaterial,
    customGlassUniforms,
  };
};

export {
  createGlassGradientMaterial,
  createPhongGradientMaterial,
  createHorizontalGlassGradientMaterial,
  createHorizontalPhongGradientMaterial,
};
